import type { Breadcrumb } from "@sentry/react";

/**
 * Everything that removes secrets before they leave the browser.
 *
 * Two sinks consume this: Sentry (`beforeBreadcrumb`) and PostHog
 * (`before_send`). Neither vendor scrubs event payloads for us — PostHog's
 * ingestion-side PII scrubber covers its Logs product only — so this file is
 * the whole control.
 *
 * A secret arrives by one of three routes, and each needs a different
 * mechanism:
 *
 * 1. under a sensitive key, anywhere in the payload       -> key matching
 * 2. embedded in free text, e.g. a backend error quoting a DSN -> shape matching
 * 3. in a query parameter, which `$current_url` puts on every event -> parameter matching
 *
 * The file is split into GENERIC helpers, which take any string or object, and
 * SPECIFIC entry points, which know one vendor's event shape. Only the
 * SPECIFIC section may assume a payload layout.
 */

// ===========================================================================
// GENERIC — operates on any string, value, or object. No event shape assumed.
// ===========================================================================

/**
 * GENERIC. Keys whose value is secret wherever it appears. Matching is
 * substring and case-insensitive, so "pass" covers "password" and "key" covers
 * "apiKey".
 *
 * Substring matching has no word boundary, so a key shorter than about four
 * characters can match an unrelated word. That rules out some obvious
 * additions: "pin" matches "mapping" and "shipping", "hash" matches the
 * non-secret "queryHash", "session" would redact `$session_id` and break
 * replay linkage, and "url" would redact `$current_url` wholesale, which is
 * why route 3 redacts parameters rather than whole URLs. "routingnumber" is
 * spelled out because a bare "routing" matches network routing.
 */
export const KEYS_TO_SCRUB = [
  "key",
  "pass",
  "pword",
  "pwd",
  "secret",
  "private",
  "ssn",
  "ccard",
  "creditcard",
  "cvv",
  "token",
  "credential",
  "access",
  "auth",
  // A Postgres DSN carries its password inline, so the value is secret even
  // though the key name mentions no credential. Nothing above matches
  // "connectionString", the variable every pgSrcConfig mutation sends.
  "connectionstring",
  "connstring",
  "dsn",
  // Multi-factor and recovery material. Recovery codes are long-lived, which
  // makes them the highest-value secret in this list.
  "recoverycode",
  "mfa",
  "otp",
  "totp",
  // An invite link's hash authenticates the invitee, like a reset token.
  "invitehash",
  // PKCE and challenge/response material.
  "challenge",
  "verifier",
  "nonce",
  // Kafka SASL config carries a username and password.
  "sasl",
  // A presigned URL is itself a bearer credential.
  "presigned",
  "bearer",
  "jwt",
  "cookie",
  // A webhook URL is a credential: holding it is enough to post as us.
  "webhook",
  // TLS and key material.
  "cert",
  "pem",
  "pfx",
  "seed",
  "salt",
  "signature",
  "digest",
  // Bank details, alongside the card keys above.
  "iban",
  "routingnumber",
];

export const REDACTED = "********";

/** GENERIC. True when a key name implies its value is secret. */
export const isSensitiveKey = (key: string): boolean => {
  const lowered = key.toLowerCase();
  return KEYS_TO_SCRUB.some((candidate) => lowered.includes(candidate));
};

// Only "=" separates a key from its value here. A colon was tried and removed:
// in prose, "bad dsn: host=db" reads as a label, and matching it swallowed the
// rest of the line. A real JSON object reaches `scrubValue` as an object, which
// matches by key already.
//
// The value is quoted or bare, bounded by the delimiters that end a value in
// prose, a query string, or a bracketed list.
//
// The value alternation accepts a leading "Bearer " because that token contains
// a space: bounding at whitespace redacted `auth=Bearer` and left the token
// after it in the clear, which a canary run caught.
const ASSIGNMENT =
  /\b([A-Za-z_][A-Za-z0-9_.-]*)(\s*=\s*)("[^"]*"|'[^']*'|(?:[Bb]earer\s+)?[^\s&;,?'"})\]]+)/g;

// Both markers this file emits, so a second pass does not re-redact.
const IS_REDACTED = /^["']?\*{3,}["']?$/;

// The libpq branch runs its value to whitespace, so once a parameter has been
// redacted its captured value is the marker plus whatever follows. Matching the
// marker as a prefix stops that branch re-redacting and swallowing the rest of
// the query string.
const STARTS_REDACTED = /^["']?\*{3,}/;

/**
 * GENERIC. Redacts credentials embedded in free text, where there is no key to
 * match on: a backend error arrives as one string with the secret in prose,
 * e.g. "failed to parse postgresql://admin:hunter2@db.example.com:5432/app".
 *
 * Both patterns are deliberately greedy. Under-redacting ships a live password;
 * over-redacting a URL that happens to look like userinfo costs nothing.
 */
export const redactCredentials = (text: string) =>
  text
    // Userinfo. The password half accepts anything but whitespace, including
    // "/" and "@", and the greedy match runs to the last "@" in the token —
    // a password may legally contain both. Whitespace still bounds the match,
    // so two connection strings in one message redact independently.
    .replace(/([a-zA-Z][\w+.-]*:\/\/)[^\s:@]*:[^\s]*@/g, "$1***:***@")
    // Key/value DSNs (`host=db password=hunter2`). libpq accepts whitespace
    // around "=" and single-quoted values, and spells the key several ways.
    // Whitespace is libpq's delimiter, so the unquoted branch runs to it and
    // not to "&": stopping at "&" left the tail of `password=hunt&er2` in the
    // clear. The quoted branches consume backslash escapes for the same
    // reason. Over-consuming a following `sslmode=require` is the safe
    // direction.
    .replace(
      /\b((?:ssl)?(?:password|passwd|pwd))\s*=\s*('(?:\\.|[^'\\])*'|"(?:\\.|[^"\\])*"|\S*)/gi,
      (match, key: string, value: string) =>
        STARTS_REDACTED.test(value) ? match : `${key}=***`,
    )
    // Any other `key=value` whose key the shared list calls sensitive. The libpq
    // branch above only knows password spellings, which is why a measured
    // canary run redacted `pwd=` but left `pass=`, `token=` and `apiKey=` in
    // the clear. The key is captured as a whole token and then tested with
    // `isSensitiveKey`, so `apiKey` matches even though "key" has no word
    // boundary inside it, which a widened alternation could not do.
    //
    // Over-redacting a non-secret assignment such as `accessLevel: read` is
    // accepted for the same reason the patterns above are greedy.
    .replace(
      ASSIGNMENT,
      (match, key: string, separator: string, value: string) =>
        isSensitiveKey(key) && !IS_REDACTED.test(value)
          ? `${key}${separator}${REDACTED}`
          : match,
    );

/**
 * GENERIC. Redacts the token after an `Authorization: Bearer` style prefix.
 * Key matching cannot reach it when the header sits under a key such as
 * `headers`, and it has no userinfo or `password=` shape for
 * `redactCredentials` to find.
 */
export const redactBearerTokens = (text: string) =>
  text.replace(/\b(bearer)\s+[A-Za-z0-9._~+/=-]{8,}/gi, `$1 ${REDACTED}`);

/**
 * GENERIC. Redacts secrets recognisable by issuer prefix, whatever key they sit
 * under and whatever prose surrounds them. This is the only mechanism that
 * catches a secret with an innocuous key name in unstructured text, and it is
 * what would have caught the Slack webhooks and tokens already found in this
 * repository's history.
 *
 * PostHog's own project token (`phc_`) is deliberately absent: it is public,
 * ships in the bundle, and authenticates every capture request.
 */
const KNOWN_SECRET_PATTERNS: RegExp[] = [
  // Slack bot, user, app and legacy tokens, plus incoming webhook URLs.
  /\bxox[abprs]-[A-Za-z0-9-]{8,}/g,
  /\bxapp-\d-[A-Za-z0-9-]{8,}/g,
  /hooks\.slack\.com\/services\/[A-Za-z0-9/_-]+/g,
  // GitHub fine-grained and classic tokens.
  /\bgithub_pat_[A-Za-z0-9_]{20,}/g,
  /\bgh[pousr]_[A-Za-z0-9]{20,}/g,
  // Stripe secret and restricted keys.
  /\b(?:sk|rk)_(?:live|test)_[A-Za-z0-9]{10,}/g,
  // OpenAI keys.
  /\bsk-(?:proj-)?[A-Za-z0-9_-]{20,}/g,
  // PostHog personal API key, as opposed to the public phc_ project token.
  /\bphx_[A-Za-z0-9]{20,}/g,
  // AWS access key id. The matching secret key has no prefix to anchor on.
  /\bAKIA[0-9A-Z]{16}\b/g,
  // A JWT, which carries its own claims and is often pasted raw.
  /\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{4,}/g,
  // A PEM block. Its body contains spaces and newlines, so the assignment
  // pattern can only ever clip the header off it.
  /-----BEGIN [A-Z ]*PRIVATE KEY-----[\s\S]*?-----END [A-Z ]*PRIVATE KEY-----/g,
];

export const redactKnownSecretPatterns = (text: string) =>
  KNOWN_SECRET_PATTERNS.reduce(
    (current, pattern) => current.replace(pattern, REDACTED),
    text,
  );

/**
 * GENERIC. Redacts the value of a sensitive query or fragment parameter.
 *
 * Rewrites textually rather than via `URL`: the input is often not a parseable
 * URL on its own, and a `URL` round-trip re-encodes unrelated parameters,
 * which would change values we are not redacting.
 *
 * A separator is required before the key so that libpq's space-delimited
 * `host=db password=hunter2` is left to `redactCredentials`; matching a bare
 * `key=` here would also catch the `=` inside an already-redacted value.
 */
//
// The value runs to the next well-formed parameter rather than to the next "&".
// An "&" not followed by `name=` does not start a parameter, so it belongs to
// the value: without this, an unencoded "&" inside a secret splits it and only
// the head is redacted, which is the same tail-survives problem the libpq
// branch above was written to stop. A correctly encoded value uses %26 and is
// unaffected either way.
const URL_PARAM =
  /([?&#])([^=&#?\s]+)=((?:[^&#\s]|&(?![A-Za-z_%][A-Za-z0-9_.%-]*=))*)/g;

// A key may be percent-encoded, and `tok%65n` must still match "token".
// Malformed escapes make `decodeURIComponent` throw, so fall back to the raw
// key rather than dropping the parameter.
const decodeKey = (key: string): string => {
  try {
    return decodeURIComponent(key);
  } catch {
    return key;
  }
};

export const redactUrlParams = (text: string): string =>
  text.replace(
    URL_PARAM,
    (match, separator: string, key: string, value: string) =>
      value.length > 0 && isSensitiveKey(decodeKey(key))
        ? `${separator}${key}=${REDACTED}`
        : match,
  );

/**
 * GENERIC. Applies every free-text mechanism to one string.
 *
 * Dispatch is per whitespace-delimited token because whitespace delimits both
 * grammars at once: a URL cannot contain a space, and whitespace is libpq's
 * own delimiter. An earlier version split the whole string on "&" whenever it
 * contained a query string anywhere, which let a URL in one part of a message
 * break the libpq rule in another — `host=db password=hunt&er2` came out as
 * `password=***&er2`, leaving the tail of the password in the clear.
 *
 * Inside a URL token, parameters are matched before `redactCredentials`, whose
 * libpq branch runs to whitespace and would otherwise swallow the rest of the
 * query.
 */
export const scrubText = (value: string): string =>
  redactBearerTokens(redactKnownSecretPatterns(value))
    // The capturing split keeps the whitespace, so the message is rebuilt
    // exactly as it came in.
    .split(/(\s+)/)
    .map((token) =>
      token.includes("?")
        ? redactCredentials(redactUrlParams(token))
        : redactCredentials(token),
    )
    .join("");

// Bounds the walk against a cyclic or pathological payload. Real events nest to
// about five levels: `properties.$exception_list[].stacktrace.frames[]`.
const MAX_DEPTH = 12;

/**
 * GENERIC. Deep-walks any value, redacting by key, by free-text shape, and by
 * query parameter.
 *
 * `inherited` carries a sensitive ancestor key down the tree, so `credentials:
 * { aws: { id: "..." } }` redacts every leaf beneath it rather than only a leaf
 * whose own name matches.
 *
 * `reserved` names keys to pass through untouched at the current level only,
 * for a vendor field that must survive. It does not apply to nested levels,
 * where a same-named key is ours.
 */
export const scrubValue = (
  value: unknown,
  { depth = 0, inherited = false, reserved = new Set<string>() } = {},
): unknown => {
  if (depth > MAX_DEPTH) return value;

  if (typeof value === "string") {
    return inherited && value.length > 0 ? REDACTED : scrubText(value);
  }

  // A number can be a secret, such as a CVV, but a boolean never is: knowing
  // `hasApiKey: true` leaks nothing, and redacting it would lose the signal.
  if (typeof value === "number") {
    return inherited && value ? REDACTED : value;
  }

  if (Array.isArray(value)) {
    return value.map((item) =>
      scrubValue(item, { depth: depth + 1, inherited }),
    );
  }

  if (value !== null && typeof value === "object") {
    const result: Record<string, unknown> = {};
    for (const [key, child] of Object.entries(value)) {
      if (reserved.has(key)) {
        result[key] = child;
        continue;
      }
      result[key] = scrubValue(child, {
        depth: depth + 1,
        inherited: inherited || isSensitiveKey(key),
      });
    }
    return result;
  }

  return value;
};

// ===========================================================================
// SPECIFIC — each function knows one vendor's event shape.
// ===========================================================================

/**
 * SPECIFIC to a Sentry `Breadcrumb`. Scrubs the GraphQL variables that
 * ApolloClient's SentryLink attaches as a JSON string on `data.variables`.
 *
 * That link is gone (apollo-link-sentry is no longer a dependency), so nothing
 * attaches variables today. It stays wired as `beforeBreadcrumb` in
 * issueReporter.ts so that anything which does start attaching them is scrubbed
 * by default rather than after the next leak.
 */
export const breadcrumbSanitize = (breadcrumb: Breadcrumb) => {
  if (breadcrumb.data && breadcrumb.data.variables) {
    try {
      breadcrumb.data.variables = scrubValue(
        JSON.parse(breadcrumb.data.variables || "{}"),
      );
    } catch {
      throw new Error(
        `Error in log sanitization with operation: ${breadcrumb.data?.operationName}`,
      );
    }
  }
  return breadcrumb;
};

// PostHog authenticates a capture request with `properties.token`, so redacting
// it rejects the event. Only the top level of `properties` is reserved; a
// `token` we set anywhere else is ours and must still be scrubbed.
const RESERVED_POSTHOG_KEYS = new Set(["token"]);

type AnalyticsEventLike = {
  event?: string;
  properties?: Record<string, unknown> | null;
};

/**
 * SPECIFIC to a PostHog capture payload. Scrubs `properties`, which is where
 * every route lands: a sensitive key, an exception message or stack frame, and
 * `$current_url`.
 *
 * Must run for every event, not just `$exception`: `$current_url` rides on
 * autocapture, web vitals and rageclicks too.
 */
export const scrubAnalyticsEvent = <T extends AnalyticsEventLike>(
  event: T,
): T => {
  if (!event.properties) return event;

  return {
    ...event,
    properties: scrubValue(event.properties, {
      reserved: RESERVED_POSTHOG_KEYS,
    }) as Record<string, unknown>,
  };
};
